import type { UseFormGetValues, UseFormSetValue } from "react-hook-form";
import type { FormData, ProviderSchema } from "../types";

export function isConstSchemaProperty(property: unknown): boolean {
  if (!property || typeof property !== "object") {
    return false;
  }

  return "const" in property;
}

export function getSchemaConstValue(
  property: unknown,
): string | number | boolean {
  return (property as { const: string | number | boolean }).const;
}

export function getSchemaConstStringValue(property: unknown): string {
  const value = getSchemaConstValue(property);
  return value === undefined || value === null ? "" : String(value);
}

export function getVisibleCredentialFieldKeys(
  schema: ProviderSchema | undefined,
): string[] {
  const properties = schema?.properties?.credentials?.properties;
  if (!properties) {
    return [];
  }

  return Object.keys(properties).filter(
    (key) => key !== "type" && !isConstSchemaProperty(properties[key]),
  );
}

export function getConstCredentialFields(
  schema: ProviderSchema | undefined,
): Array<{ key: string; value: string }> {
  const properties = schema?.properties?.credentials?.properties;
  if (!properties) {
    return [];
  }

  return Object.entries(properties)
    .filter(([, property]) => isConstSchemaProperty(property))
    .map(([key, property]) => ({
      key,
      value: getSchemaConstStringValue(property),
    }));
}

export function isMerchantIdConstInSchema(
  schema: ProviderSchema | undefined,
): boolean {
  return isConstSchemaProperty(
    schema?.properties?.acquirer?.properties?.merchantId,
  );
}

export function shouldShowMerchantIdField(
  schema: ProviderSchema | undefined,
): boolean {
  const merchantIdProperty =
    schema?.properties?.acquirer?.properties?.merchantId;
  return Boolean(merchantIdProperty) && !isMerchantIdConstInSchema(schema);
}

export interface SchemaConstPayloadValues {
  code?: string;
  priority?: number;
  merchantId?: string;
  credentials: Record<string, string>;
}

export function getSchemaConstPayloadValues(
  schema: ProviderSchema | undefined,
): SchemaConstPayloadValues {
  const credentials: Record<string, string> = {};
  let priority: number | undefined;
  let merchantId: string | undefined;
  let code: string | undefined;

  const codeProperty = schema?.properties?.code;
  if (isConstSchemaProperty(codeProperty)) {
    code = getSchemaConstStringValue(codeProperty);
  }

  const credentialProperties = schema?.properties?.credentials?.properties;
  if (credentialProperties) {
    Object.entries(credentialProperties).forEach(([key, property]) => {
      if (!isConstSchemaProperty(property)) {
        return;
      }
      credentials[key] = getSchemaConstStringValue(property);
    });
  }

  const priorityProperty = schema?.properties?.priority;
  if (isConstSchemaProperty(priorityProperty)) {
    priority = Number(getSchemaConstValue(priorityProperty));
  }

  const merchantIdProperty =
    schema?.properties?.acquirer?.properties?.merchantId;
  if (isConstSchemaProperty(merchantIdProperty)) {
    merchantId = getSchemaConstStringValue(merchantIdProperty);
  }

  return {
    code,
    priority,
    merchantId,
    credentials,
  };
}

export function applySchemaConstValuesToForm(
  schema: ProviderSchema | undefined,
  setValue: UseFormSetValue<FormData>,
  getValues?: UseFormGetValues<FormData>,
): void {
  const constValues = getSchemaConstPayloadValues(schema);

  if (constValues.priority !== undefined) {
    setValue("priority", constValues.priority, { shouldValidate: false });
  }

  if (constValues.merchantId !== undefined) {
    setValue("merchantId", constValues.merchantId, { shouldValidate: false });
  }

  if (Object.keys(constValues.credentials).length > 0) {
    const currentCredentials = getValues?.("credentials") ?? {};
    setValue(
      "credentials",
      {
        ...currentCredentials,
        ...constValues.credentials,
      },
      { shouldValidate: false },
    );
  }

  Object.entries(constValues.credentials).forEach(([key, value]) => {
    if (key === "type") {
      setValue("type", value, { shouldValidate: false });
    }
  });
}
